import { ApolloError } from '@apollo/client';
import { Stack } from '@mui/material';
import { compact, difference } from 'lodash';
import * as React from 'react';
import { SubmitHandler } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { ModalFormLayout } from '@/components/form/layout/ModalFormLayout';
import { FormModal } from '@/components/modals/FormModal/FormModal';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { Loader } from '@/components/progress/Loader/Loader';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { AssetIntegrationField } from '@/modules/assets/AssetIntegrationField/AssetIntegrationField';
import {
  AssetIntegrationFormShape,
  defaultValues,
} from '@/modules/assets/AssetIntegrationField/AssetIntegrationField.types';
import { EntityType } from '@/modules/entities/types/EntityType';
import { Operations as EstatePageOperations } from '@/modules/estate/graphql/EstatePageData.generated';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { diagnostics } from '@/utils/diagnostics';

import { useLinkIntegrationEntityMutation } from './graphql/LinkIntegrationEntityFormModal.generated';
import { useLinkIntegrationFormData } from './LinkIntegrationEntityFormModal.utils';

export interface LinkIntegrationEntityFormProps {
  entityType: EntityType;
}

function LinkIntegrationEntityForm({
  entityType,
}: LinkIntegrationEntityFormProps) {
  const { householdId } = useHouseholdDetailsContext();

  return (
    <div
      onClick={(event: React.MouseEvent) => {
        // Prevent form interactions from bubbling up to underlying components
        event.stopPropagation();
      }}
    >
      <ModalFormLayout>
        <Stack
          spacing={2}
          onClick={(event: React.MouseEvent) => {
            // Additional safety: prevent any form interactions from bubbling up
            event.stopPropagation();
          }}
        >
          <AssetIntegrationField
            entityType={entityType}
            householdId={householdId!}
            hideManualAssets
            alwaysShowToggles
          />
        </Stack>
      </ModalFormLayout>
    </div>
  );
}
export interface LinkIntegrationEntityFormModalProps {
  entityId: string;
  open: boolean;
  onClose: () => void;
}

export function LinkIntegrationEntityFormModal({
  entityId,
  open,
  onClose,
}: LinkIntegrationEntityFormModalProps) {
  const { showFeedback } = useFeedback();

  const formMethods = useForm<AssetIntegrationFormShape>({
    defaultValues: defaultValues,
  });

  const {
    loading: loadingFormData,
    previousIntegrationEntityIds,
    entityType,
  } = useLinkIntegrationFormData(entityId, open, formMethods.reset);

  const [linkIntegrationEntity, { loading }] = useLinkIntegrationEntityMutation(
    {
      refetchQueries: [EstatePageOperations.Query.EstatePageData],
      onCompleted: () => {
        showFeedback('Successfully updated entity data linking', {
          variant: 'success',
        });
        onClose();
      },
      onError: (error: ApolloError) => {
        diagnostics.error('Failed to update entity data linking', error);
        showFeedback('Failed to update entity data linking');
      },
    }
  );

  const onValidSubmission: SubmitHandler<AssetIntegrationFormShape> = (
    formValues
  ) => {
    // TODO: T2-6341 the empty state entities are actually pre-created entities so this won't be falsy
    if (!entityId) {
      onClose();
      return;
    }

    const nextIntegrationEntityIds = compact(formValues.integrationEntityIds);

    // Calculate which integration entities to add
    const addedIntegrationEntityIds = difference(
      nextIntegrationEntityIds,
      previousIntegrationEntityIds
    );

    const removedIntegrationEntityIds = difference(
      previousIntegrationEntityIds,
      nextIntegrationEntityIds
    );

    return linkIntegrationEntity({
      variables: {
        entityId: entityId,
        addedIntegrationEntityIds: addedIntegrationEntityIds,
        removedIntegrationEntityIds: removedIntegrationEntityIds,
        addeparLinkedToNongrantorEntity: formValues.linkToAllAddeparEntities,
        groupIntegrationAssetValues: !formValues.showAllIntegratedHoldings,
      },
    });
  };
  const onSubmit = formMethods.handleSubmit(onValidSubmission);

  return (
    <FormProvider {...formMethods}>
      <FormModal
        heading="Link data"
        isOpen={open}
        onClose={onClose}
        fullHeight={false}
        actions={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                void onSubmit();
              }}
              loading={loading}
            >
              Link holdings
            </Button>
          </>
        }
      >
        {loadingFormData || !entityType ? (
          <Stack alignItems="center" justifyContent="center" height="100%">
            <Loader />
          </Stack>
        ) : (
          <LinkIntegrationEntityForm entityType={entityType} />
        )}
      </FormModal>
    </FormProvider>
  );
}
